import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { normalizeTaskTags } from '../../../../hooks/useTagActions';
import useOnTaskIdentity from '../../hooks/useOnTaskIdentity';

/**
 * useTagsDropdown Hook
 * 
 * Manages all state and handlers for the TagsDropdown component.
 * Provides optimistic UI updates for instant feedback.
 */
export const useTagsDropdown = ({
  taskId,
  task,
  allTags,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
}) => {
  // Menu state
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  
  // Search state
  const [searchValue, setSearchValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  
  // Edit state
  const [editingTag, setEditingTag] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Local tags for instant UI updates
  const [localTags, setLocalTags] = useState(() => normalizeTaskTags(task));

  useOnTaskIdentity(taskId, () => {
    setShowMenu(false);
    setSearchValue('');
    setEditingTag(null);
    setDeleteConfirm(null);
    setLocalTags(normalizeTaskTags(task));
  });
  
  // Refs
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);
  const editInputRef = useRef(null);

  // Derive current tags - use local when menu open for instant updates
  const actualTags = normalizeTaskTags(task);
  const currentTags = showMenu ? localTags : actualTags;
  const primaryTag = currentTags.length > 0 ? currentTags[0] : null;
  
  // Sync local state when menu opens
  useEffect(() => {
    if (showMenu) {
      setLocalTags(normalizeTaskTags(task));
    }
  }, [showMenu, task]);

  // Filter available tags
  const filteredTags = useMemo(() => {
    const search = searchValue.toLowerCase().trim();
    return allTags.filter(tag => {
      if (currentTags.includes(tag.name)) return false;
      if (search && !tag.name.toLowerCase().includes(search)) return false;
      return true;
    });
  }, [allTags, currentTags, searchValue]);

  // Can create new tag?
  const canCreateTag = useMemo(() => {
    const search = searchValue.trim();
    if (!search) return false;
    return !allTags.some(t => t.name.toLowerCase() === search.toLowerCase());
  }, [searchValue, allTags]);

  // Focus search when menu opens
  useEffect(() => {
    if (showMenu && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 10);
    }
  }, [showMenu]);

  // Focus edit input when editing
  useEffect(() => {
    if (editingTag && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingTag]);

  // Reset highlight when results change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredTags.length, canCreateTag]);

  // Unique ID for this dropdown instance
  const dropdownId = useRef(`tags-${taskId}`).current;

  // Handlers - define closeMenu before effects that use it
  const closeMenu = useCallback(() => {
    setShowMenu(false);
    setSearchValue('');
    setHighlightedIndex(0);
    setEditingTag(null);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      closeMenu();
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showMenu, closeMenu]);

  // Close on scroll
  useEffect(() => {
    if (!showMenu) return;
    
    const handleScroll = (e) => {
      // Don't close if scrolling inside the menu itself
      if (menuRef.current?.contains(e.target)) return;
      closeMenu();
    };
    
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showMenu, closeMenu]);

  // Close when another dropdown opens (only listen when this menu is open)
  useEffect(() => {
    if (!showMenu) return;
    
    const handleCloseDropdowns = (e) => {
      if (e.detail?.exceptId !== dropdownId) {
        closeMenu();
      }
    };
    
    window.addEventListener('closeTableDropdowns', handleCloseDropdowns);
    return () => window.removeEventListener('closeTableDropdowns', handleCloseDropdowns);
  }, [showMenu, dropdownId, closeMenu]);

  // Close when canvas starts panning (trackpad or mouse drag)
  useEffect(() => {
    if (!showMenu) return;

    const handlePanStart = () => closeMenu();
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [showMenu, closeMenu]);

  const openMenu = useCallback((e) => {
    e.stopPropagation();
    
    if (!showMenu) {
      // Close other dropdowns before opening this one
      window.dispatchEvent(new CustomEvent('closeTableDropdowns', { detail: { exceptId: dropdownId } }));

      const menuWidth = 320; // Match CSS width
      let left = e.clientX;
      if (left + menuWidth > window.innerWidth - 20) {
        left = window.innerWidth - menuWidth - 20;
      }
      if (left < 8) left = 8;

      setMenuPosition({ top: e.clientY + 6, left, anchor: e.clientY });
      setShowMenu(true);
    } else {
      closeMenu();
    }
  }, [showMenu, closeMenu, dropdownId]);

  // Reposition menu after render to handle viewport boundaries
  useEffect(() => {
    if (!showMenu || !menuRef.current) return undefined;
    const anchor = menuPosition.anchor ?? menuPosition.top;

    const frame = requestAnimationFrame(() => {
      const menuRect = menuRef.current?.getBoundingClientRect();
      if (!menuRect) return;

      const viewportHeight = window.innerHeight;
      const padding = 20;

      if (menuRect.bottom > viewportHeight - padding) {
        const topAbove = anchor - menuRect.height - 6;
        const top = topAbove >= padding ? topAbove : viewportHeight - menuRect.height - padding;
        setMenuPosition((prev) => ({ ...prev, top }));
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [showMenu, menuPosition.anchor, menuPosition.top]);

  const handleSelectTag = useCallback((tagName) => {
    // Instant local update
    setLocalTags(prev => [...prev, tagName]);
    // Notify parent
    onAddTag?.(taskId, tagName);
    setSearchValue('');
    searchInputRef.current?.focus();
  }, [taskId, onAddTag]);

  const handleRemoveTag = useCallback((tagName) => {
    // Instant local update
    setLocalTags(prev => prev.filter(t => t !== tagName));
    // Notify parent
    onRemoveTag?.(taskId, tagName);
  }, [taskId, onRemoveTag]);

  const handleCreateTag = useCallback(() => {
    const trimmed = searchValue.trim();
    if (trimmed) {
      // Instant local update
      setLocalTags(prev => [...prev, trimmed]);
      // Notify parent
      onAddTag?.(taskId, trimmed);
      setSearchValue('');
      searchInputRef.current?.focus();
    }
  }, [searchValue, taskId, onAddTag]);

  const handleSetPrimary = useCallback((tagName) => {
    onSetPrimaryTag?.(taskId, tagName);
  }, [taskId, onSetPrimaryTag]);

  const handleKeyDown = useCallback((e) => {
    const totalItems = filteredTags.length + (canCreateTag ? 1 : 0);
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => (prev + 1) % Math.max(totalItems, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev - 1 + Math.max(totalItems, 1)) % Math.max(totalItems, 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (canCreateTag && highlightedIndex === 0) {
          handleCreateTag();
        } else {
          const tagIndex = canCreateTag ? highlightedIndex - 1 : highlightedIndex;
          if (filteredTags[tagIndex]) {
            handleSelectTag(filteredTags[tagIndex].name);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeMenu();
        break;
      case 'Backspace':
        if (!searchValue && currentTags.length > 0) {
          handleRemoveTag(currentTags[currentTags.length - 1]);
        }
        break;
      default:
        break;
    }
  }, [filteredTags, canCreateTag, highlightedIndex, handleCreateTag, handleSelectTag, closeMenu, searchValue, currentTags, handleRemoveTag]);

  // Edit handlers
  const handleStartEdit = useCallback((e, tag) => {
    e.stopPropagation();
    setEditingTag(tag.name);
    setEditingName(tag.name);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingTag && editingName.trim() && onRenameTag) {
      onRenameTag(editingTag, editingName.trim());
    }
    setEditingTag(null);
    setEditingName('');
  }, [editingTag, editingName, onRenameTag]);

  const handleCancelEdit = useCallback(() => {
    setEditingTag(null);
    setEditingName('');
  }, []);

  const handleRequestDelete = useCallback((e, tagName) => {
    e.stopPropagation();
    setDeleteConfirm({ tagName });
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirm && onDeleteTag) {
      onDeleteTag(deleteConfirm.tagName);
    }
    setDeleteConfirm(null);
  }, [deleteConfirm, onDeleteTag]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirm(null);
  }, []);

  return {
    // State
    showMenu,
    menuPosition,
    searchValue,
    highlightedIndex,
    editingTag,
    editingName,
    deleteConfirm,
    currentTags,
    primaryTag,
    filteredTags,
    canCreateTag,
    
    // Refs
    btnRef,
    menuRef,
    searchInputRef,
    editInputRef,
    
    // Setters
    setSearchValue,
    setHighlightedIndex,
    setEditingName,
    
    // Handlers
    openMenu,
    closeMenu,
    handleSelectTag,
    handleRemoveTag,
    handleCreateTag,
    handleSetPrimary,
    handleKeyDown,
    handleStartEdit,
    handleSaveEdit,
    handleCancelEdit,
    handleRequestDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};

export default useTagsDropdown;

